import { devices } from '@src/styles/theme';
import { useRouter } from 'next/router';
import React from 'react';
import styled from 'styled-components';

/**
 * 커뮤니티 목록 상단 (Pple Community 디자인 이식).
 *
 * 디자인 탭은 전체/수혈요청/헌혈후기/질문/감사인사 였으나,
 * '수혈 요청'은 Board가 아니라 Donation 도메인이고 '감사 인사' 카테고리는 백엔드에 없다.
 * 그래서 실제 Board.Category(헌혈 후기/헌혈의 집/질문/자유)에 맞춰 탭을 구성한다.
 */
export const BOARD_TABS = [
  { value: '', label: '전체' },
  { value: 'BLOOD_DONATION_REVIEW', label: '헌혈 후기' },
  { value: 'BLOOD_HOME_REVIEW', label: '헌혈의 집' },
  { value: 'QNA', label: '질문' },
  { value: 'FREE', label: '자유' },
];

type Props = {
  /** 디자인의 글쓰기 컴포저 바(아바타 + 입력 유사 영역 + 글쓰기 버튼) */
  composerName?: string;
  activeTab: string;
  onTabChange: (value: string) => void;
  totalCount?: number;
  keyword: string;
  onKeywordChange: (v: string) => void;
  onSearch: () => void;
};

const BoardListHeader = ({
  composerName,
  activeTab,
  onTabChange,
  totalCount,
  keyword,
  onKeywordChange,
  onSearch,
}: Props) => {
  const router = useRouter();

  return (
    <Wrap>
      <TitleRow>
        <Title>커뮤니티</Title>
        <SubText>헌혈 경험을 나누고, 도움이 필요한 이웃을 만나요</SubText>
      </TitleRow>

      <TabRow>
        <Tabs>
          {BOARD_TABS.map((tab) => (
            <Tab
              key={tab.label}
              active={activeTab === tab.value}
              onClick={() => onTabChange(tab.value)}
            >
              {tab.label}
            </Tab>
          ))}
        </Tabs>
        <SearchBox>
          <SearchIcon />
          <SearchInput
            value={keyword}
            placeholder="게시글 검색"
            onChange={(e) => onKeywordChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSearch();
            }}
          />
        </SearchBox>
      </TabRow>

      {/* 글쓰기 컴포저 바 (디자인 Community) */}
      <Composer onClick={() => router.push('/community/edit?type=board')}>
        <ComposerAvatar>{composerName?.trim()?.[0] ?? '나'}</ComposerAvatar>
        <ComposerInput>오늘의 헌혈 이야기를 들려주세요</ComposerInput>
        <WriteButton
          onClick={(e) => {
            e.stopPropagation();
            router.push('/community/edit?type=board');
          }}
        >
          글쓰기
        </WriteButton>
      </Composer>
    </Wrap>
  );
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const TitleRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
`;
const Title = styled.h1`
  margin: 0;
  font-family: 'Var', sans-serif;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.6px;
  color: #222222;
  @media ${devices.tablet} {
    font-size: 24px;
  }
`;
const SubText = styled.span`
  font-size: 14px;
  color: #767676;
  padding-bottom: 5px;
`;
const WriteButton = styled.button`
  flex: none;
  height: 44px;
  padding: 0 22px;
  border: none;
  border-radius: 22px;
  background: #ff4651;
  color: #ffffff;
  font-size: 14.5px;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background: #e03a44;
  }
`;
const TabRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;
const Tabs = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;
const Tab = styled.button<{ active: boolean }>`
  height: 38px;
  padding: 0 16px;
  border-radius: 19px;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid ${({ active }) => (active ? '#FF4651' : '#E6E6E6')};
  background: ${({ active }) => (active ? '#FFF1ED' : '#ffffff')};
  color: ${({ active }) => (active ? '#FF4651' : '#767676')};
`;
const SearchBox = styled.div`
  margin-left: auto;
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #e6e6e6;
  border-radius: 19px;
  padding: 0 14px;
  height: 38px;
  width: 210px;
  @media ${devices.tablet} {
    margin-left: 0;
    width: 100%;
  }
`;
const SearchIcon = styled.span`
  flex: none;
  width: 14px;
  height: 14px;
  border: 1.6px solid #9a9a9a;
  border-radius: 50%;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    right: -4px;
    bottom: -4px;
    width: 6px;
    height: 1.6px;
    background: #9a9a9a;
    transform: rotate(45deg);
  }
`;
const SearchInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-family: 'Regular', sans-serif;
  font-size: 13.5px;
  color: #222222;
  width: 100%;
`;
/** 디자인의 글쓰기 컴포저 바 */
const Composer = styled.div`
  background: #ffffff;
  border: 1px solid #ededed;
  border-radius: 20px;
  padding: 18px 22px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  @media ${devices.tablet} {
    padding: 14px 16px;
    border-radius: 16px;
  }
`;
const ComposerAvatar = styled.div`
  width: 42px;
  height: 42px;
  flex: none;
  border-radius: 21px;
  background: #ffe3dc;
  color: #ff4651;
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ComposerInput = styled.div`
  flex: 1;
  min-width: 0;
  height: 44px;
  border-radius: 22px;
  background: #f4f4f4;
  padding: 0 18px;
  display: flex;
  align-items: center;
  font-size: 14.5px;
  color: #9a9a9a;
`;
const ResultCount = styled.span`
  font-size: 14px;
  color: #767676;
  b {
    color: #222222;
  }
`;

export default BoardListHeader;
